<template>
<div class="container-fluid">
	<img v-if="!data_loaded" class="center-me" src="/images/loading-color.svg">

	<template v-else>
		<div class="row">
		    <div class="col">
				<h1 data-test="pageTitle">Edit Client: {{ client.name }}</h1>
			</div>
		</div>

		<form @submit.prevent="submitForm" @keydown="form.onKeydown($event)" @change="form.onKeydown($event)" class="mb-0">
			<alert-errors :form="form">There were some problems with your input.</alert-errors>
			<div class="row">		
			    <div v-if="perms.can_edit_client" class="col-12">


				<div class="card">
					<div class="d-flex" style="min-height:400px">

						<div class="card-side-menu bg-gray-100 rounded-left" style="min-width:150px;">
							<div class="card-header bg-gray-600 text-white" style="border-top-right-radius: 0px; border-bottom:1px solid #6c757d;">Settings</div>
							<div class="flex-column p-2">
								<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'profile'}" @click="setPanel('profile')" data-test="selectProfileTab">Profile</span>
								<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'members'}" @click="setPanel('members')" data-test="selectMembersTab">Members</span>
								<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'contact'}" @click="setPanel('contact')" data-test="selectContactTab">Main Contact</span>
								<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'subscribers'}" @click="setPanel('subscribers')" data-test="selectSubscribersTab">Subscribers</span>
								<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'location'}" @click="setPanel('location')" data-test="selectLocationTab">Location</span>
								<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'connections'}" @click="setPanel('connections')" v-if="perms.can_do_everything" data-test="selectConnectionsTab">Connections</span>
								<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'passwords'}" @click="setPanel('passwords')" v-if="perms.can_administer_password_reqs" data-test="selectPasswordsTab">Passwords</span>
							</div>
						</div>


						<div v-show="active_panel == 'profile'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
							<div class="card-header">Profile</div>
							<div class="card-body row">
								<div v-if="client.status != 1" class="col-12">
									<div class="rounded bg-red-100 text-red-700 text-sm p-25 mb-35 leading-tight font-weight-bold">
										This client is disabled.
									</div>
								</div>

								<div class="col-md-auto" style="max-width:200px;">
									<label class="mb-0">Logo</label>
									<file-field
										ref="logo"
										:default_url="client.logo_url"
										@file_changed="changedLogo($event)"
									/>
							    </div>

								<div v-if="perms.can_do_everything" class="col">	
									<div class="form-group">
										<label class="mb-0">Client Name</label>
										<input class="form-control" v-model="form.name" required data-test="clientName">
									</div>

								    <div class="form-group">
								    	<label class="mb-0">Status</label>
				                        <v-select 
				                        	:clearable="false"       
				                            :options="[
				                            	{ label:'Active', value:1 },
				                            	{ label:'Inactive', value:0 }
				                            ]"
				                            :reduce="label => label.value"
				                            v-model="form.status"
				                            data-test="clientStatus"
				                        />
								    </div>

								    <div class="form-group">
								    	<label class="mb-0">Organization</label>
				                        <v-select   
				                            :options="all_orgs"
				                            label="name"
				                            :reduce="name => name.id"
				                            v-model="form.org_id"
				                            :clearable="true"
				                            placeholder="Assign to organization - Optional"
				                            data-test="clientOrganization"
				                        />
								    </div>								    

								    <div class="form-group">
								    	<label class="mb-0">Theme / White Label</label>
				                        <v-select        
				                            :options="[
				                            	{ label:'Default / None', value:null },
				                            	{ label:'FlightPlan', value:'flightplan' }
				                            ]"
				                            :reduce="label => label.value"
				                            v-model="form.theme"
				                            data-test="clientTheme"
				                        />
								    </div>

								    <div class="form-group">
									    <label class="mb-0">Service Lines</label>
				                        <v-select
				                            multiple
				                            label="label"
				                            :options="options.service_lines"
				                            :reduce="label => label.id"
				                            v-model="form.service_lines"
				                            data-test="clientServiceLines"
				                        />
								    </div>
								</div>

								<div v-else class="col">
									<div class="form-group">
										<label class="mb-0">Client Name</label>
										<input class="form-control" v-model="form.name" required data-test="clientName">
									</div>
									<div v-if="client.org_id" class="form-group">
										<label class="mb-0 text-gray-600">Organization</label>
										<div class="font-weight-bold">{{ client.org.name }}</div>
									</div>
									<div class="form-group">
										<label class="mb-0 text-gray-600">Service Lines</label>
										<ul class="pl-35 font-weight-bold">
											<li v-for="service_line in client.service_lines">{{ service_line.label }}</li>
										</ul>
									</div>																					
								</div>

								<div class="col-12 text-right">
									<button type="submit" class="btn btn-primary" data-test="saveClientProfileButton">Save Profile</button>
								</div>
							</div>
						</div>


						<div v-show="active_panel == 'members'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
							<div class="card-header">Members</div>
							<div class="card-body">
								<div class="form-group" data-test="clientMembersList">
									
									<div v-for="user in users.filter(user => client.users.map(user => user.id).includes(user.id))" class="d-flex form-check mb-2">
										<input class="form-check-input mt-1" 
											type="checkbox"
											v-model="form.users"
											:value="user.id"
											:disabled="user.id == $store.state.user.id && client.id == $store.state.user.client_id"
										>
										<div class="rounded-circle avatar-xs circle-cropped mr-2">
											<img class="img-fluid" :src="user.photo_url">
										</div>
										<div>
											{{ user.display_name }}
											<span v-for="role in user.roles" class="badge bg-blue-100 text-blue-700 mr-1">{{ role.name }}</span>
										</div>
									</div>
								</div>
								<div class="form-group" data-test="availableUsersList">
									<div class="text-blue-700 font-weight-bold">Available Users</div>
									<div v-for="user in users.filter(user => !client.users.map(user => user.id).includes(user.id))" class="d-flex form-check mb-2">
										<input class="form-check-input mt-1" type="checkbox" v-model="form.users" :value="user.id">
										<div class="rounded-circle avatar-xs circle-cropped mr-2">
											<img class="img-fluid" :src="user.photo_url">
										</div>
										<div>
											{{ user.display_name }}
											<span v-for="role in user.roles" class="badge bg-blue-100 text-blue-700 mr-1">{{ role.name }}</span>
										</div>
									</div>
								</div>										
								<div class="text-right">
									<button type="submit" class="btn btn-primary" data-test="saveClientMembersButton">Save Users</button>
								</div>								
							</div>
						</div>


						<div v-show="active_panel == 'contact'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
							<div class="card-header">Main Contact</div>
							<div class="card-body">
								<div class="form-row">
								    <div class="col form-group">
										<label class="mb-0">First Name</label>
										<input class="form-control" v-model="form.contact_first_name" data-test="clientContactFirstName">
								    </div>
								    <div class="col form-group">
										<label class="mb-0">Last Name</label>
										<input class="form-control" v-model="form.contact_last_name" data-test="clientContactLastName">
								    </div>
								</div>
							    <div class="form-group">
									<label class="mb-0">Email</label>
									<input class="form-control" type="email" v-model="form.contact_email" data-test="clientContactEmail">
							    </div>
							    <div class="form-row">				    	    
								    <div class="col form-group">
										<label class="mb-0">Phone</label>
										<input class="form-control" type="tel" v-model="form.phone" data-test="clientContactPhone">
								    </div>
								    <div class="col form-group">
										<label class="mb-0">Fax</label>
										<input class="form-control" type="tel" v-model="form.fax" data-test="clientContactFax">
								    </div>
								</div>
								<div class="text-right">
									<button type="submit" class="btn btn-primary" data-test="saveClientContactButton">Save Profile</button>
								</div>															
							</div>
						</div>


						<div v-show="active_panel == 'subscribers'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
							<div class="card-header">Weekly Digest Subscribers</div>
							<div class="card-body">
								<div class="row">
									<div class="form-group col-6">
										<div class="text-blue-700 font-weight-bold">Members</div>
											<div v-for="user in client.users" class="form-check">
												<input type="checkbox" class="form-check-input" :value="user.email" v-model="digest_subscribers" disabled>
												<label class="form-check-label"><span class="text-gray-700">{{ user.display_name }}</span></label>
											</div>
											<div class="mt-2 text-gray-500 text-sm">For information only. Users manage their subscriptions.</div>
									</div>

									<div class="form-group col-6">
										<div class="d-flex align-items-baseline mb-2">
											<div class="text-blue-700 font-weight-bold flex-fill mb-0 mr-1">Extra subscribers <span class="text-gray-500 font-weight-normal">- Optional</span></div>
											<span
								                @click="addExtraSubscriber()"
								                v-tooltip.bottom="'Add Recipient'"
								                class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
								                data-test="addSubscriberButton" >
								                <i class="far fa-plus-circle text-base" />
								            </span>
								        </div>


										<div v-for="(email, index) in extra_digest_subscribers" class="d-flex align-items-baseline">
											<input
												v-model="extra_digest_subscribers[index]"
												placeholder="email address"
												type="email"
												class="form-control flex-fill mb-2 mr-1"
												data-test="subscriberEmail"
											/>
											<span
								                @click="removeExtraSubscriber(index)"
								                data-toggle="tooltip" data-placement="bottom" title="Remove Recipient"
								                class="btn btn-sm btn-ghost-primary text-gray-500 cursor-pointer leading-none"
								                data-test="removeSubscriberButton" >
								                <i class="far fa-minus-circle text-base" />
								            </span>
										</div>

										<small class="form-text text-gray-500">Non-users can receive the digest by adding their email address. Remove recipients by clearing the text field. With great power comes great responsibility!</small>							
									</div>

									<div class="col-12 text-right">
										<span class="btn btn-primary" @click="submitSubscriptions()" data-test="saveClientSubscriptionButton">Save Subscriptions</span>
									</div>
								</div>

							</div>							
						</div>


						<div v-show="active_panel == 'location'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
							<div class="card-header">Location</div>
							<div class="card-body row">
								<div class="col-md-auto" style="max-width:300px;">
									<label class="mb-0">Facility or Background Image</label>
									<file-field
										ref="photo"
										:default_url="client.photo_url"
										@file_changed="changedPhoto($event)"
									/>
							    </div>

								<div class="col">
								    <div class="form-group">
								    	<label class="mb-0">Address</label>
								    	<input class="form-control mb-2" v-model="form.address" data-test="clientLocationAddress">					    	
								    	<input class="form-control" v-model="form.address_2" data-test="clientLocationAddress2">	
								    </div>

								    <div class="row">		
									    <div class="form-group col-12">
									    	<label class="mb-0">City</label>
								    		<input class="form-control" v-model="form.city" data-test="clientLocationCity">
									    </div>
									</div>
									<div class="form-row">
									    <div class="form-group col-6">
										    <label class="mb-0">State</label>
						                    <v-select
						                        label="label"
						                        :select-on-key-codes="[9, 13]"
						                        :options="options.states"
						                        :reduce="label => label.id"
						                        v-model="form.state"
						                        data-test="clientLocationState"
						                    />
									    </div>
									    <div class="form-group col-6">
									    	<label class="mb-0">Zip</label>
								    		<input class="form-control" type="number" v-model="form.zip" data-test="clientLocationZip">
									    </div>
									</div>
								</div>

								<div class="col-12 text-right">
									<button type="submit" class="btn btn-primary" data-test="saveClientLocationButton">Save Location</button>
								</div>
							</div>
						</div>


						<div v-if="perms.can_do_everything" v-show="active_panel == 'connections'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
					    	<div class="card-header">Connections</div>
						    <div class="card-body">
							    <div class="form-group">
									<label class="mb-0">EMR Type</label>
			                        <v-select        
			                            :options="[
			                            	{ label:'Luvo EMR', value:'luvo_emr' }
			                            ]"
			                            :reduce="label => label.value"
			                            v-model="form.emr_type"
			                        />
							    </div>
							    <div class="form-group">
									<label class="mb-0">EMR ID</label>
									<input class="form-control" v-model="form.emr_id">
							    </div>
							    <div class="form-group form-check">
									<input type="checkbox" class="form-check-input" v-model="form.import_emr_physicians" :false-value="0" :true-value="1">
									<label class="form-check-label">Import and sync EMR physicians with Ember</label>
							    </div>

							    <div class="text-blue-700 font-weight-bold pb-1">Reporting</div>

							    <div class="form-group">
									<label class="mb-0">Quicksight Username</label>
									<input class="form-control" v-model="form.quicksight_username" data-test="clientConnectionsQuicksight">
							    </div>
							    <div class="form-group">
									<label class="mb-0">AWS Key</label>
									<input class="form-control" v-model="form.aws_key" data-test="clientConnectionsAwsKey">
							    </div>
							    <div class="form-group">
									<label class="mb-0">AWS Secret Key</label>
									<input class="form-control" v-model="form.aws_secret_key" data-test="clientConnectionsAwsSecret">
							    </div>

								<div class="text-right">
									<button type="submit" class="btn btn-primary" data-test="saveClientConnectionsButton">Save Connections</button>
								</div>							    				    					    					    				    	
						    </div>
						</div>


						<div v-if="perms.can_administer_password_reqs" v-show="active_panel == 'passwords'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
							<div class="card-header">Password Requirements</div>
							<div class="card-body">
								<div class="rounded bg-yellow-100 text-yellow-800 text-sm p-25 mb-25 leading-tight">
									Requirements are enforced when a user changes their password. Existing passwords will not be effected.
								</div>

							    <div class="form-group form-check">
									<input type="checkbox" class="form-check-input" v-model="pw_form.length_enforced" data-test="clientPasswordsLengthEnforced">
									<label class="form-check-label">Require minimum length</label>
									<div v-if="pw_form.length_enforced" class="mt-2">
										<label class="mb-0 text-sm">Minimum characters</label>
										<input class="form-control w-auto" v-model="pw_form.min_length" type="number" required data-test="clientPasswordsLength">
									</div>
							    </div>

							    <div class="form-group form-check">
									<input type="checkbox" class="form-check-input" v-model="pw_form.history_enforced" data-test="clientPasswordsHistoryEnforced">
									<label class="form-check-label">Enforce password history</label>
									<div v-if="pw_form.history_enforced" class="mt-2">
										<label class="mb-0 text-sm">Number before re-use permitted</label>
										<input class="form-control w-auto" v-model="pw_form.pass_history" type="number" required data-test="clientPasswordsHistory">
									</div>
							    </div>

							    <div class="form-group form-check">
									<input type="checkbox" class="form-check-input" v-model="pw_form.mixed_case" :false-value="0" :true-value="1" data-test="clientPasswordsMixedCase">
									<label class="form-check-label">Require mixed case</label>
							    </div>

							    <div class="form-group form-check">
									<input type="checkbox" class="form-check-input" v-model="pw_form.number" :false-value="0" :true-value="1" data-test="clientPasswordsNumber">
									<label class="form-check-label">Require a number</label>
							    </div>

							    <div class="form-group form-check">
									<input type="checkbox" class="form-check-input" v-model="pw_form.special_char" :false-value="0" :true-value="1" data-test="clientPasswordsSpecialChar">
									<label class="form-check-label">Require a special character</label>
							    </div>

								<div class="text-right">
									<span class="btn btn-primary" @click="submitPasswordReqs()" data-test="saveClientPasswordsButton">Save Password Reqs</span>
								</div>															
							</div>
						</div>						
					</div>
				</div>

				<div class="row">
					<div class="col-12">		            
					    <div class="form-group">
					    	<span class="btn btn-ghost-secondary mr-1" @click="cancelEdit()">Cancel</span>				    	
						</div>
					</div>
				</div>

			</div>
		</div>

		</form>
	</template>
</div>
</template>

<script>
	import { mapState } from 'vuex';
 	import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
	import { Form, HasError, AlertError, AlertErrors, AlertSuccess } from 'vform';
	import { objectToFormData } from 'object-to-formdata'

    export default {
    	mixins: [BreadcrumbsManager],

    	components: {
            HasError,
            AlertError,
            AlertErrors,
            AlertSuccess,
            Form    		
    	},

        data () {
            return {
            	active_panel: 'profile',
            	all_orgs: [],
                client: [],
                data_loaded: false,
                form: {},
                pw_form: {},
                pending: false,
                users: [],

                digest_subscribers: [], //derived from existing
                extra_digest_subscribers: [], //derived from existing
            }
        },

        created() {
        	this.$store.dispatch('fetchUserIds');	
        },

        activated() {
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Clients', to: { name: 'clients.index' } },
                { text: '[Client Name]', to: { name: 'clients.show' } },
            ]);  

        	this.fetchClient();
			this.fetchOrgIds();
        	this.fetchUsers();              
        },

        methods: {
        	addExtraSubscriber() {
        		this.extra_digest_subscribers.push('');
        	},

            changedPhoto($event) {
                this.form.photo = this.$refs.photo.file;
            },

            changedLogo($event) {
                this.form.logo = this.$refs.logo.file;
            },

        	fetchClient() {
        		axios.get('/api/admin/clients/' + this.$route.params.id ).then(({data}) => {
                    this.setClientData(data);
                    this.fetchSubscriptions();
                    this.replaceBreadcrumb({
                        find: '[Client Name]',
                        replace: { text: this.client.name }
                    });
                    this.data_loaded = true;
	        	});
        	},

            fetchOrgIds() {
            	if (this.perms.can_do_everything) {
					axios.get('/api/admin/orgs/ids').then(({data}) => {
						this.all_orgs = data;
					});
				}
            },

        	fetchSubscriptions() {
        		axios.get('/api/subscriptions/client/' + this.$route.params.id ).then(({data}) => {
        			this.setSubscriptions(data);
	        	});
        	},

			fetchUsers() {
				axios.get('/api/admin/users').then(({data}) => {
					this.users = data;
				});
			},

        	// rebuildFormSubscriptions() {
        	// 	this.form.subscriptions.weekly_digest = [...this.digest_subscribers, ...this.extra_digest_subscribers];
        	// },

        	removeExtraSubscriber(index) {
        		this.extra_digest_subscribers.splice(index, 1);
        	},

        	setClientData(data) {
                this.client = data;

                if (this.perms.can_do_everything) {
	                this.form = new Form({
	                    name: data.name,
						logo: data.logo,
						users: data.users.map(user => user.id),
						photo: data.photo,
						address: data.address,
						address_2: data.address_2,
						city: data.city,
						state: data.state,
						zip: data.zip,
						contact_first_name: data.contact_first_name,
						contact_last_name: data.contact_last_name,
						phone: data.phone,
						fax: data.fax,
						contact_email: data.contact_email,

	                    status: data.status,
						theme: data.theme,
						org_id: data.org_id,
						service_lines: data.service_lines.map(line => line.id),
						emr_type: data.emr_type,
						emr_id: data.emr_id,
						import_emr_physicians: data.import_emr_physicians,
						quicksight_username: data.quicksight_username,
						aws_key: data.aws_key,
						aws_secret_key: data.aws_secret_key,
					}); 
                } else {
	                this.form = new Form({
	                    name: data.name,
						logo: data.logo,
						users: data.users.map(user => user.id),
						photo: data.photo,
						address: data.address,
						address_2: data.address_2,
						city: data.city,
						state: data.state,
						zip: data.zip,
						contact_first_name: data.contact_first_name,
						contact_last_name: data.contact_last_name,
						phone: data.phone,
						fax: data.fax,
						contact_email: data.contact_email,
	                });                	
                }

                if (this.perms.can_administer_password_reqs) {
                	this.pw_form = new Form({
                		min_length: 		data.password_reqs ? data.password_reqs.min_length : false,
                		mixed_case: 		data.password_reqs ? data.password_reqs.mixed_case : false,
                		number: 			data.password_reqs ? data.password_reqs.number : false,
                		special_char: 		data.password_reqs ? data.password_reqs.special_char : false,
                		pass_history: 		data.password_reqs ? data.password_reqs.pass_history : false,

                		length_enforced: 	data.password_reqs ? data.password_reqs.min_length : false,
                		history_enforced: 	data.password_reqs ? data.password_reqs.pass_history : false
                	});
                }
        	},

            setPanel(value) {
            	this.active_panel = value;
            },

            setSubscriptions(data) {
				this.digest_subscribers = [];
    			this.extra_digest_subscribers = [];
				data.filter(sub => sub.subscribed_to == 'weekly_digest').forEach(sub => {
					if (this.client.users.find(user => user.email === sub.email)) {
						if (sub.status === 1) {
							this.digest_subscribers.push(sub.email);
						}
					}
					else {
						this.extra_digest_subscribers.push(sub.email);
					}
				})
            },

        	submitForm() { 
            	this.pending = true;

                this.form.submit('post', '/api/admin/clients/' + this.$route.params.id, {
					transformRequest: [function (data, headers) {
						data['_method'] = 'PUT';
						return objectToFormData(data)
					}],
                }).then(({ data }) => {
					Swal.fire({
						toast: true,
						position: 'top-end',
						showConfirmButton: false,
						timer: 4000,
						icon: 'success',
						title: 'Client saved',
					});	

					this.setClientData(data);
                	this.pending = false;
            	}).catch((error) => { 
            		this.pending = false;
            	});
        	},

        	submitPasswordReqs() {
        		this.pending = true;
        		this.pw_form.submit('post', '/api/admin/clients/' + this.$route.params.id + '/password-reqs').then(({data}) => {
					Swal.fire({
						toast: true,
						position: 'top-end',
						showConfirmButton: false,
						timer: 4000,
						icon: 'success',
						title: 'Password requirements saved',
					});
        			this.pending = false;
        		});
        	},

        	submitSubscriptions() {
        		this.pending = true;
        		axios.post('/api/subscriptions/client/' + this.$route.params.id, { subscriptions: { weekly_digest: this.extra_digest_subscribers } }).then(({data}) => {
					Swal.fire({
						toast: true,
						position: 'top-end',
						showConfirmButton: false,
						timer: 4000,
						icon: 'success',
						title: 'Subscriptions saved',
					});

        			this.setSubscriptions(data);
        			this.pending = false;
        		});
        	}

        },

        computed: {
        	...mapState(['options', 'perms'])
        },

        // watch: {
        // 	digest_subscribers() {
        // 		this.rebuildFormSubscriptions();
        // 	},
        // 	extra_digest_subscribers() {
        // 		this.rebuildFormSubscriptions();
        // 	},
        // }
    }
</script>

<style>
</style>